import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { MailtoLink, Form, Button } from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';

import { COURSE_CREATOR_STATES } from '../../constants';
import { useHelpUrls } from '../../help-urls/hooks';
import { HelpSidebar, HelpSidebarLink } from '../../generic/help-sidebar';
import { getStudioHomeData } from '../data/selectors';
import messages from './messages';
import TaskStatusChecker from './TaskStatusChecker';

const HomeSidebar = ({ onRunCourseAggregation }) => {
  const [showAllActions, setShowAllActions] = React.useState(false);
  const location = useLocation();
  const intl = useIntl();
  const {
    studioName,
    platformName,
    studioShortName,
    studioRequestEmail,
    techSupportEmail,
    courseCreatorStatus,
  } = useSelector(getStudioHomeData);
  const { home: aboutHomeLink } = useHelpUrls(['home']);
  // NOTE: This check does not update if a user's permission is changed. The user will have to log out and back in.
  const isStaff = getAuthenticatedUser().administrator;

  // eslint-disable-next-line max-len
  const isShowMailToGetInstruction = courseCreatorStatus === COURSE_CREATOR_STATES.disallowedForThisSite
  && !!studioRequestEmail;
  const isShowUnrequestedInstruction = courseCreatorStatus === COURSE_CREATOR_STATES.unrequested;
  const isShowDeniedInstruction = courseCreatorStatus === COURSE_CREATOR_STATES.denied;

  // Check if we're on the programs page
  const isPathwaysPath = location.pathname === '/pathways';

  // If on programs page, show programs-specific sidebar
  if (isPathwaysPath) {
    return (
      <HelpSidebar>
        <h4 className="help-sidebar-about-title">
          {intl.formatMessage(messages.programsTitle)}
        </h4>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsDescription)}
        </p>
        <h5 className="help-sidebar-about-title">
          {intl.formatMessage(messages.programsPlatformsTitle)}
        </h5>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsPlatformsDescription1)}
        </p>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsPlatformsDescription2)}
        </p>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsPlatformsDescription3)}
        </p>
        <h5 className="help-sidebar-about-title">
          {intl.formatMessage(messages.programsProgramsTitle)}
        </h5>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsProgramsDescription)}
        </p>
        <h6 className="help-sidebar-about-title">
          {intl.formatMessage(messages.programsCreatingTitle)}
        </h6>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsCreatingDescription1)}
        </p>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsCreatingDescription2)}
        </p>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsCreatingDescription3)}
        </p>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsCreatingDescription4)}
        </p>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsCreatingDescription5)}
        </p>
        <h6 className="help-sidebar-about-title">
          {intl.formatMessage(messages.programsEditingTitle)}
        </h6>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsEditingDescription1)}
        </p>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsEditingDescription2)}
        </p>
        <p className="help-sidebar-about-descriptions">
          {intl.formatMessage(messages.programsEditingDescription3)}
        </p>
      </HelpSidebar>
    );
  }

  // Default sidebar content for home/libraries/other pages
  return (
    <HelpSidebar>
      <h4 className="help-sidebar-about-title">
        {intl.formatMessage(messages.aboutTitle, { studioName })}
      </h4>
      <p className="help-sidebar-about-descriptions">
        {intl.formatMessage(messages.aboutDescription, { studioShortName })}
      </p>
      <HelpSidebarLink
        as="span"
        pathToPage={aboutHomeLink || ''}
        title={intl.formatMessage(messages.studioHomeLinkToGettingStarted, {
          studioName,
        })}
      />
      {isShowMailToGetInstruction && (
        <>
          <hr />
          <h4 className="help-sidebar-about-title">
            {intl.formatMessage(messages.sidebarHeader2, { studioName })}
          </h4>
          <p className="help-sidebar-about-descriptions">
            {intl.formatMessage(messages.sidebarDescription2, {
              studioName,
              mailTo: (
                <MailtoLink to={studioRequestEmail}>
                  {intl.formatMessage(messages.sidebarDescription2MailTo, {
                    platformName,
                  })}
                </MailtoLink>
              ),
            })}
          </p>
        </>
      )}
      {isShowUnrequestedInstruction && (
        <>
          <hr />
          <h4 className="help-sidebar-about-title">
            {intl.formatMessage(messages.sidebarHeader3, { studioName })}
          </h4>
          <p className="help-sidebar-about-descriptions">
            {intl.formatMessage(messages.sidebarDescription3, { studioName })}
          </p>
        </>
      )}
      {isShowDeniedInstruction && (
        <>
          <hr />
          <h4 className="help-sidebar-about-title">
            {intl.formatMessage(messages.sidebarHeader4, { studioName })}
          </h4>
          <p className="help-sidebar-about-descriptions">
            {intl.formatMessage(messages.sidebarDescription4, {
              studioName,
              mailTo: (
                <MailtoLink to={techSupportEmail}>
                  {intl.formatMessage(messages.sidebarDescription4MailTo, {
                    platformName,
                  })}
                </MailtoLink>
              ),
            })}
          </p>
        </>
      )}
      <hr />
      {isStaff && (
        // Do not render for nonstaff users
        <>
          <h4 className="help-sidebar-about-title">
            {intl.formatMessage(messages.additionalActionsTitle)}
          </h4>
          <p className="help-sidebar-about-descriptions">
            {intl.formatMessage(messages.additionalActionsDescription)}
          </p>
          <div>
            <Form.Checkbox
              checked={showAllActions}
              onChange={(e) => setShowAllActions(e.target.checked)}
              style={{ fontSize: '14px' }}
            >
              <span style={{ fontSize: '14px' }}>{intl.formatMessage(messages.showAllActionsCheckbox)}</span>
            </Form.Checkbox>
          </div>

          {showAllActions && onRunCourseAggregation && (
            <div className="mt-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={onRunCourseAggregation}
                data-testid="run-course-aggregation-button"
              >
                {intl.formatMessage(messages.runCourseAggregationButton)}
              </Button>
            </div>
          )}
          <hr style={{ marginTop: '0.75rem', marginBottom: '0' }} />
          <TaskStatusChecker />
        </>
      )}

    </HelpSidebar>
  );
};

HomeSidebar.propTypes = {
  onRunCourseAggregation: PropTypes.func,
};

HomeSidebar.defaultProps = {
  onRunCourseAggregation: null,
};

export default HomeSidebar;
